import type { DeviceFacts, JobView, ScriptTemplateView, TemplateParameter } from '@api/devices/contracts';
import { runbookUnavailableReason } from '@api/operations/runbook-compatibility';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { useEffect, useState } from 'react';
import { getScriptTemplate, isRunbookUpdateRequired, isStatus, listScriptTemplates } from '../lib/api';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { type DeviceRef, useJobActions } from './jobs';
import { ReviewList } from './meta';

// The Script library (WRMM-230): the workspace's own PowerShell templates. Running one requests an ordinary script job
// with the exact bytes of the version shown here, approved per job like any other.

export function useScriptTemplates(enabled = true) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'script-templates'], enabled, queryFn: ({ signal }) => listScriptTemplates(mspId, signal) });
}
export function useScriptTemplate(id: string | null, version?: number) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'script-template', id, version ?? 'current'], enabled: !!id,
    queryFn: ({ signal }) => getScriptTemplate(mspId, id!, version, signal) });
}

/** The same bounds the API applies (resolveParameters), so the button is only offered for values it will take. */
export const parameterValid = (p: TemplateParameter, value: unknown) => p.type === 'integer'
  ? typeof value === 'number' && Number.isSafeInteger(value) && value >= p.min && value <= p.max
  : typeof value === 'string' && value.length >= p.minLength && value.length <= p.maxLength && value.trim() === value && !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(value);

/** Run a template on one device: its exact bytes and review, a value for each parameter, then the usual approval. */
export function TemplateRunDialog({ template, device, agent, open, onOpenChange, onNeedsApproval }: {
  template: ScriptTemplateView | null; device: DeviceRef; agent?: DeviceFacts['agent']; open: boolean; onOpenChange: (open: boolean) => void;
  onNeedsApproval: (job: JobView) => void;
}) {
  const { create } = useJobActions();
  const { mspId } = useScope();
  const client = useQueryClient();
  const detail = useScriptTemplate(open && template ? template.id : null);
  // Someone saved a newer version since the list was read: refresh the list, which brings this dialog the new version's
  // parameters and review together (the values typed so far start over, as they were for the old bytes).
  const newer = !!template && !!detail.data && detail.data.version !== template.version;
  useEffect(() => {
    if (newer) void client.invalidateQueries({ queryKey: ['scope', mspId, 'script-templates'] });
  }, [newer, client, mspId]);
  const [params, setParams] = useState<Record<string, number | string>>({});
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  useEffect(() => {
    if (!open || !template) return;
    setParams(Object.fromEntries(template.parameters.map(p => [p.name, p.default ?? ''])));
    setReason(template.defaultReason || template.name); setRefusal(null);
  }, [open, template]);
  if (!template) return null;
  const shown = detail.data?.shown;
  // The bytes asked for must be the ones on screen: the version this list showed, fetched and matching its hash.
  const ready = !!shown && shown.version === template.version && shown.scriptSha256 === template.scriptSha256;
  const unavailable = runbookUnavailableReason(template, agent);
  const valid = template.parameters.every(p => parameterValid(p, params[p.name])) && !!reason.trim();
  const submit = () => create.mutate({ deviceId: device.id, reason: reason.trim(), template: { id: template.id, version: template.version, parameters: params } }, {
    onSuccess: job => { onOpenChange(false); if (job.state === 'awaiting_approval') onNeedsApproval(job); },
    onError: error => setRefusal(isStatus(error, 409) && !isRunbookUpdateRequired(error)
      ? 'This template changed or was archived since you opened it. Close this and open it again to review the current version.' : null),
  });
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(680px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold">{template.name}</Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">{template.description || 'A template from your Script library.'}</Dialog.Description>
            </div>
            <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5 text-[12px]">
            <span className="rounded-md bg-sunken px-2 py-0.5 font-mono text-ink-2">{device.title}</span>
            <span className="rounded-md bg-attention-soft px-2 py-0.5 text-attention">Needs its own approval</span>
            <span className="rounded-md bg-sunken px-2 py-0.5 text-ink-2">{template.runAs === 'system' ? 'Runs as SYSTEM' : 'Runs as the signed-in user'} · v{template.version}</span>
            <span className="rounded-md bg-sunken px-2 py-0.5 font-mono text-ink-3" title={template.scriptSha256}>SHA-256 {template.scriptSha256.slice(0, 12)}…</span>
          </div>
          <div className="mt-4 space-y-3">
            {template.parameters.map(p => (
              <label key={p.name} className="flex items-center justify-between gap-3 text-[13.5px]">
                <span className="text-ink-2">{p.label} <span className="font-mono text-[12px] text-ink-3">${p.name}</span></span>
                {p.type === 'integer'
                  ? <input type="number" min={p.min} max={p.max} value={params[p.name] ?? p.default} aria-label={p.label}
                      onChange={e => setParams(v => ({ ...v, [p.name]: Number(e.target.value) }))}
                      className="h-9 w-24 rounded-lg border border-line bg-panel px-2 text-right tabular outline-none focus:border-brand/50" />
                  : <input type="text" required={p.minLength > 0} minLength={p.minLength} maxLength={p.maxLength} value={params[p.name] ?? ''} aria-label={p.label}
                      onChange={e => setParams(v => ({ ...v, [p.name]: e.target.value }))}
                      className="h-9 w-64 rounded-lg border border-line bg-panel px-2 outline-none focus:border-brand/50" />}
              </label>
            ))}
            <label className="block text-[13.5px]"><span className="text-ink-2">Reason</span>
              <input value={reason} onChange={e => setReason(e.target.value)} maxLength={500} aria-label="Reason"
                className="mt-1 h-9 w-full rounded-lg border border-line bg-panel px-3 outline-none focus:border-brand/50" /></label>
            <div className="rounded-lg border border-line px-3 py-2 text-[13px]">
              <p className="font-medium text-ink-2">What it runs</p>
              <div className="mt-2"><ReviewList findings={template.review} compact /></div>
              {shown ? <pre aria-label="Template script" className="mt-2 max-h-64 overflow-auto rounded-lg bg-sunken p-2.5 font-mono text-[12px]">{shown.script}</pre>
                : <p className="mt-2 text-ink-3">{detail.isError ? 'The script couldn’t be loaded.' : 'Loading the script…'}</p>}
              <p className="mt-1.5 text-[12px] text-ink-3">The values above are passed to its param() block as data, never as code. The approval covers these exact bytes and values.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost">Cancel</Button></Dialog.Close>
              <Button variant="primary" disabled={create.isPending || !ready || !valid || !!unavailable} onClick={submit}><ShieldCheck className="size-4" />Request approval</Button>
            </div>
            {newer && <p role="status" className="text-[13px] text-attention">This template has a newer version (v{detail.data!.version}); loading it so you review what will run.</p>}
            {unavailable && <p role="status" className="text-[13px] text-attention">{unavailable}</p>}
            {refusal && <p role="alert" className="text-[13px] text-offline">{refusal}</p>}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
